import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Divider,
  Alert,
  CircularProgress
} from '@mui/material';
import { Analytics } from '@mui/icons-material';

/**
 * SMART Analysis confirmation dialog
 */
const SmartAnalysisDialog = ({ 
  open, 
  onConfirm, 
  onClose,
  savedValidatedCount,
  unsavedValidatedCount,
  totalRequirements,
  analyzing = false,
  error = '',
  hasPermission = true
}) => {
  const canAnalyze = savedValidatedCount > 0 && unsavedValidatedCount === 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Analytics color="primary" />
          <Typography variant="h6" component="span">
            SMART Analysis
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        {hasPermission ? (
          <>
            <Typography variant="body1" gutterBottom>
              Execute the SMART analysis on validated requirements. This will evaluate whether the requirements meet the SMART criteria: <strong>Specific</strong>, <strong>Measurable</strong>, <strong>Achievable</strong>, <strong>Relevant</strong>, and <strong>Time-bound</strong>.
            </Typography>
            
            <Divider sx={{ my: 2 }} />
            
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" gutterBottom>
                Analysis Statistics:
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body1" color="text.secondary">
                    Total Requirements:
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {totalRequirements}
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body1" color="text.secondary">
                    Saved Validated Requirements:
                  </Typography>
                  <Typography variant="body1" fontWeight="bold" color="success.main">
                    {savedValidatedCount}
                  </Typography>
                </Box>
                
                {unsavedValidatedCount > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body1" color="text.secondary">
                      Unsaved Validated Requirements:
                    </Typography>
                    <Typography variant="body1" fontWeight="bold" color="warning.main">
                      {unsavedValidatedCount}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>

            {unsavedValidatedCount > 0 && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                There are {unsavedValidatedCount} unsaved validated requirement(s). Please save the changes before running the analysis.
              </Alert>
            )}
            
            {savedValidatedCount === 0 && (
              <Alert severity="info" sx={{ mt: 2 }}>
                No validated requirements found. Please validate at least one requirement before running the analysis.
              </Alert>
            )}
            
            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </>
        ) : (
          <Typography color="error" variant="body1">
            You do not have permission to perform SMART analysis.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={analyzing}>
          Cancel
        </Button>
        <Button 
          onClick={onConfirm} 
          variant="contained" 
          disabled={!canAnalyze || analyzing || !hasPermission}
          startIcon={analyzing ? <CircularProgress size={20} color="inherit" /> : <Analytics />}
        >
          {analyzing ? 'Analyzing...' : 'Start Analysis'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SmartAnalysisDialog;
